import { useState } from "react";
import Wrapper, { WrapperProps } from "src/components/field/utils/Wrapper";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import withTranslation, {
	Translation,
} from "src/components/hoc/withTranslation";
import Card from "src/components/Card";
import FileUpload from "src/components/form/file-upload/FileUpload";
import { ALLOWED_FILES } from "src/lib/constants";
import { ApiMedia } from "src/api/types";
import FileUploadItem from "src/components/form/file-upload/FileUploadItem";
import FileList from "src/components/field/utils/FileList";
import Tag from "src/components/Tag";

type Value = ApiMedia | ApiMedia[];

interface FileProps extends Translation {
	wrapper: WrapperProps;
	placeholder?: string;
	value?: Value;
	onChange: (value: Value) => void;
}

const File = ({ t, wrapper, placeholder, value, onChange }: FileProps) => {
	const [open, setOpen] = useState(false);
	const isMultiple = Array.isArray(value);
	const hasValue = isMultiple ? isMultiple && value?.length > 0 : !!value;
	const files = isMultiple ? value : [...(value ? [value] : [])];
	const [variant, setVariant] = useState<"upload" | "list">(
		hasValue ? "list" : "upload"
	);

	const handleUpload = (media: ApiMedia) => {
		if (isMultiple) {
			onChange([...value, media]);
		} else {
			onChange(media);
		}
		setVariant("list");
		setOpen(false);
	};

	return (
		<Wrapper
			{...{
				...wrapper,
			}}
		>
			<PopoverPrimitive.Root
				{...{ open }}
				onOpenChange={(v) => {
					if (open) {
						setVariant(hasValue ? "list" : "upload");
					}
					setOpen(v);
				}}
			>
				<PopoverPrimitive.Trigger asChild>
					<button
						className={`flex relative w-full items-center gap-2 border-[2px] p-1 focus:border-gray rounded-md ${
							open ? "border-gray" : "border-transparent"
						} ${!hasValue ? "text-placeholder" : "text-dark"}`}
					>
						{!hasValue && (
							<span>{placeholder || t("placeholder")}</span>
						)}
						{hasValue && (
							<div className="flex gap-2 flex-wrap">
								{files.length > 3 ? (
									<Tag theme="gray" size="default">
										{t("file-max", {
											count: files.length,
										})}
									</Tag>
								) : (
									files.map((media) => (
										<FileUploadItem
											key={`file-${media.id}`}
											{...{ media }}
										/>
									))
								)}
							</div>
						)}
					</button>
				</PopoverPrimitive.Trigger>
				<PopoverPrimitive.Content className="w-full z-[88]">
					{variant === "upload" && (
						<Card className="w-max" contentClassName=" p-4">
							<FileUpload
								onChange={handleUpload}
								allow={ALLOWED_FILES}
								value={undefined}
							/>
						</Card>
					)}
					{variant === "list" && (
						<Card className="w-max" contentClassName=" p-3">
							<FileList {...{ files, onChange }} />
							<div
								onClick={() => setVariant("upload")}
								className="py-[11px] px-[10px] rounded-md flex items-center gap-2 cursor-pointer"
							>
								<i className="far fa-plus"></i>
								<span className="font-regular">
									{t("upload")}
								</span>
							</div>
						</Card>
					)}
					<PopoverPrimitive.Arrow className="fill-dark" />
				</PopoverPrimitive.Content>
			</PopoverPrimitive.Root>
		</Wrapper>
	);
};

File.locale = {
	nl: {
		"file-max": "({{count}}) bestanden",
		placeholder: "Voeg een afbeelding of bestand toe",
		upload: "Upload",
	},
	en: {
		"file-max": "({{count}}) files",
		placeholder: "Upload an image or file",
		upload: "Upload",
	},
};

export default withTranslation(File);
